/**
 * Voice capping and priority stealing, from
 * `doc/grind/architecture/AUDIO-VISUAL-EFFECTS.md` §2.
 *
 * Twenty mobs hit at once is twenty impact sounds, and twenty oscillators
 * starting on the same frame is not twenty times as loud — it is distortion. So
 * every sound asks permission first, and the answer may be "play, but stop that
 * one" or "no".
 *
 * Pure: no `AudioContext`, no clock of its own. The mixer owns the nodes; this
 * owns the policy.
 */

/**
 * The categories the game actually emits.
 *
 * The spec's table has two more — *Pinnacle Boss Special Telegraphs* and
 * *Environmental Ambiance* — deliberately left out. Nothing places a boss and
 * there are no ambient loops, and a category with a cap and no sounds is a rule
 * that cannot be wrong because it never runs. Add each with the feature.
 *
 * `UI` is not in the spec's table either, but its bus list has `UI_Loot`: level-up
 * and death stings are neither combat nor loot, and they must not be stolen by a
 * busy fight.
 */
export type VoiceCategory = 'LOOT' | 'UI' | 'SKILL' | 'MOB_ATTACK' | 'IMPACT';

/** How a category behaves when it is full. */
export type EvictionPolicy = 'oldest' | 'furthest' | 'quietest';

export interface CategoryRule {
  /** Concurrent voices allowed. */
  cap: number;
  /** Higher wins when the *global* cap forces a choice between categories. */
  priority: number;
  /** Which of its own voices a full category gives up. */
  evict: EvictionPolicy;
}

/**
 * The spec's matrix, minus the two categories nothing emits.
 *
 * `IMPACT` is meant to "combine hits into a single pitch-shifted combo impact
 * sound". That is not implemented — combining needs a mixdown this synth does
 * not do — so it steals its quietest voice instead, which is audibly close and
 * honest about being a cap rather than a blend.
 */
export const RULES: Record<VoiceCategory, CategoryRule> = {
  LOOT: { cap: 3, priority: 100, evict: 'oldest' },
  UI: { cap: 2, priority: 90, evict: 'oldest' },
  SKILL: { cap: 4, priority: 80, evict: 'oldest' },
  MOB_ATTACK: { cap: 6, priority: 50, evict: 'furthest' },
  IMPACT: { cap: 8, priority: 35, evict: 'quietest' },
};

/**
 * Total voices allowed across every category.
 *
 * Lower than the sum of the caps (23) on purpose: those are per-category limits
 * for variety, and this is the one that protects the output. A fight that fills
 * every category at once should still not exceed what a limiter can hold.
 */
export const GLOBAL_CAP = 16;

export interface VoiceRequest {
  category: VoiceCategory;
  /** Post-attenuation gain, 0..1. Used to pick the quietest victim. */
  gain: number;
  /** World distance from the listener. Used to pick the furthest victim. */
  distance: number;
}

/** A voice currently sounding. */
export interface ActiveVoice extends VoiceRequest {
  id: number;
  /** Monotonic sequence, not a clock — the oldest voice is the lowest one. */
  seq: number;
}

/** What the limiter decided. */
export interface Admission {
  /** False when the new sound is dropped entirely. */
  admitted: boolean;
  /** A voice the mixer must stop first, or null. */
  evict: number | null;
}

const DROPPED: Admission = { admitted: false, evict: null };

/**
 * Decides which sounds may start, and which must stop to make room.
 *
 * Not reactive and not a Solid store: the mixer drives it directly, once per
 * sound.
 */
export class VoiceLimiter {
  private readonly active = new Map<number, ActiveVoice>();
  private nextId = 1;
  private nextSeq = 1;

  /** Voices currently sounding. */
  get size(): number {
    return this.active.size;
  }

  /** How many of one category are sounding. */
  countOf(category: VoiceCategory): number {
    let n = 0;
    for (const v of this.active.values()) if (v.category === category) n++;
    return n;
  }

  /**
   * Asks to start a sound.
   *
   * Returns the voice's id when admitted, along with any voice the caller must
   * stop first. A caller that ignores `evict` will exceed the cap, so the mixer
   * stops that voice before starting the new one.
   */
  admit(req: VoiceRequest): Admission & { id: number } {
    const rule = RULES[req.category];
    const incoming: ActiveVoice = { ...req, id: this.nextId, seq: this.nextSeq };

    // A full category always gives up one of its own. Every voice in it has the
    // same priority, so there is nothing to compare — the newest sound is the
    // one the player just caused, and it wins.
    let evict: number | null = null;
    if (this.countOf(req.category) >= rule.cap) {
      evict = this.victimIn(req.category, rule.evict);
    } else if (this.active.size >= GLOBAL_CAP) {
      // The global cap is the only place categories compete. A loot chime may
      // silence an impact; an impact may never silence a loot chime.
      //
      // Equal priority loses too, deliberately: with nothing to choose between
      // two sounds, keeping the one already sounding beats churning a voice for
      // no audible gain. It also makes the lowest category — impacts, the most
      // frequent sound in the game — the one that goes without when the output
      // is full, which is exactly the right sacrifice.
      const weakest = this.weakest();
      if (weakest === null || RULES[weakest.category].priority >= rule.priority) {
        return { ...DROPPED, id: 0 };
      }
      evict = weakest.id;
    }

    if (evict !== null) this.active.delete(evict);

    this.active.set(incoming.id, incoming);
    this.nextId++;
    this.nextSeq++;
    return { admitted: true, evict, id: incoming.id };
  }

  /** Reports that a voice has finished or been stopped. */
  release(id: number): void {
    this.active.delete(id);
  }

  /** Drops every voice — a zone change, or returning to the title. */
  clear(): void {
    this.active.clear();
  }

  private victimIn(category: VoiceCategory, policy: EvictionPolicy): number | null {
    let victim: ActiveVoice | null = null;
    for (const v of this.active.values()) {
      if (v.category !== category) continue;
      if (victim === null || worseThan(v, victim, policy)) victim = v;
    }
    return victim?.id ?? null;
  }

  /** The lowest-priority voice, oldest first among equals. */
  private weakest(): ActiveVoice | null {
    let weakest: ActiveVoice | null = null;
    for (const v of this.active.values()) {
      if (weakest === null) {
        weakest = v;
        continue;
      }
      const a = RULES[v.category].priority;
      const b = RULES[weakest.category].priority;
      if (a < b || (a === b && v.seq < weakest.seq)) weakest = v;
    }
    return weakest;
  }
}

/** Whether `candidate` is a better thing to silence than `current`. */
function worseThan(candidate: ActiveVoice, current: ActiveVoice, policy: EvictionPolicy): boolean {
  switch (policy) {
    case 'furthest':
      return candidate.distance > current.distance;
    case 'quietest':
      return candidate.gain < current.gain;
    default:
      return candidate.seq < current.seq;
  }
}
